// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { atomWithRefresh } from "jotai/utils";

import { logApiError } from "../infra/apiError";
import { fetchJson } from "../infra/fetchJson";

export interface RuntimeUIConfig {
  resource_custom_enabled: boolean;
  resource_preset_enabled: boolean;
  yaml_editor_enabled: boolean;
  non_installed_images: boolean;
  job_schedule_max_number_of_times: number;
  default_cuda_type: string;
}

// Defaults match workflow/siteconfig/models.py RuntimeUIConfig.
export const DEFAULT_RUNTIME_UI_CONFIG: RuntimeUIConfig = {
  resource_custom_enabled: true,
  resource_preset_enabled: true,
  yaml_editor_enabled: true,
  non_installed_images: false,
  job_schedule_max_number_of_times: 3,
  // Blank preselects no accelerator in the task form (see models.py).
  default_cuda_type: "",
};

export const RUNTIME_UI_CONFIG_QUERY_KEY = ["runtime-ui-config"];

// Sanitize the API response. Type-check every field rather than only
// null-coalescing: a wrong-typed value (e.g. the string "false" for a
// boolean) would otherwise pass through and silently flip feature behavior
// in a boolean condition. Defends against malformed responses per CLAUDE.md
// "Frontend Type Safety".
const asBool = (value: unknown, fallback: boolean): boolean =>
  typeof value === "boolean" ? value : fallback;

const asString = (value: unknown, fallback: string): string =>
  typeof value === "string" ? value : fallback;

const sanitize = (raw: Partial<RuntimeUIConfig> | null): RuntimeUIConfig => {
  const max = raw?.job_schedule_max_number_of_times;
  return {
    resource_custom_enabled: asBool(
      raw?.resource_custom_enabled,
      DEFAULT_RUNTIME_UI_CONFIG.resource_custom_enabled
    ),
    resource_preset_enabled: asBool(
      raw?.resource_preset_enabled,
      DEFAULT_RUNTIME_UI_CONFIG.resource_preset_enabled
    ),
    yaml_editor_enabled: asBool(
      raw?.yaml_editor_enabled,
      DEFAULT_RUNTIME_UI_CONFIG.yaml_editor_enabled
    ),
    non_installed_images: asBool(
      raw?.non_installed_images,
      DEFAULT_RUNTIME_UI_CONFIG.non_installed_images
    ),
    // Accept only a non-negative integer (0 is the "scheduling disabled"
    // sentinel); reject floats, negatives, NaN, and non-numbers.
    job_schedule_max_number_of_times:
      typeof max === "number" && Number.isInteger(max) && max >= 0
        ? max
        : DEFAULT_RUNTIME_UI_CONFIG.job_schedule_max_number_of_times,
    default_cuda_type: asString(
      raw?.default_cuda_type,
      DEFAULT_RUNTIME_UI_CONFIG.default_cuda_type
    ),
  };
};

export const useRuntimeUIConfig = () => {
  return useQuery({
    queryKey: RUNTIME_UI_CONFIG_QUERY_KEY,
    queryFn: async (): Promise<RuntimeUIConfig> => {
      try {
        const raw = await fetchJson<Partial<RuntimeUIConfig> | null>(
          "/api/runtime-ui-config/"
        );
        return sanitize(raw);
      } catch (e) {
        logApiError("useRuntimeUIConfig:/api/runtime-ui-config/", e);
        return DEFAULT_RUNTIME_UI_CONFIG;
      }
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: DEFAULT_RUNTIME_UI_CONFIG,
  });
};

// Async Jotai atom for non-React contexts (e.g., other atoms) that need
// the runtime-UI flags but cannot call hooks. Falls back to defaults on
// network failure so downstream atoms never block the UI. Uses
// atomWithRefresh so the update mutation can re-fetch it (see
// useUpdateRuntimeUIConfig) and keep atom consumers — e.g. imageListAtom —
// in sync with the React Query cache after an admin saves changes.
export const runtimeUIConfigAtom = atomWithRefresh(
  async (): Promise<RuntimeUIConfig> => {
    try {
      const raw = await fetchJson<Partial<RuntimeUIConfig> | null>(
        "/api/runtime-ui-config/"
      );
      return sanitize(raw);
    } catch (e) {
      logApiError("runtimeUIConfigAtom:/api/runtime-ui-config/", e);
      return DEFAULT_RUNTIME_UI_CONFIG;
    }
  }
);

export const useUpdateRuntimeUIConfig = () => {
  const queryClient = useQueryClient();
  const refreshRuntimeUIConfigAtom = useSetAtom(runtimeUIConfigAtom);
  // Uses raw useMutation intentionally — throws in mutationFn for
  // the global QueryErrorHandler to catch, rather than local toast handling.
  return useMutation({
    mutationFn: async (
      config: Partial<RuntimeUIConfig>
    ): Promise<RuntimeUIConfig> => {
      const raw = await fetchJson<Partial<RuntimeUIConfig> | null>(
        "/api/runtime-ui-config/",
        {
          method: "PATCH",
          body: JSON.stringify(config),
        }
      );
      return sanitize(raw);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(RUNTIME_UI_CONFIG_QUERY_KEY, data);
      // Re-fetch the atom so non-React consumers (imageListAtom) pick up the
      // change without a full page reload.
      refreshRuntimeUIConfigAtom();
    },
  });
};
